import type { PromptConfig } from '@bosmart/types'
import { fetchInterviewLiveToken, fetchInterviewQuestions } from '@/lib/api-client'
import { fetchInterviewPrompt } from '@/services/prompts'
import { logger } from '@/lib/logging'

export interface InterviewQuestionContext {
  id: string
  order: number
  summary: string
  prompt: string
}

interface LoadInterviewContextArgs {
  interviewId: string
  token?: string
  otpProofToken?: string
}

export async function loadInterviewContext({
  interviewId,
  token,
  otpProofToken,
}: LoadInterviewContextArgs) {
  const prompt = await loadPrompt(interviewId, token, otpProofToken)
  const questions = await loadQuestions(interviewId, token, otpProofToken)
  const liveToken = await fetchInterviewLiveToken(interviewId, token, otpProofToken)

  return { prompt, questions, liveToken }
}

async function loadPrompt(
  interviewId: string,
  token?: string,
  otpProofToken?: string
): Promise<PromptConfig | undefined> {
  try {
    logger.info('Fetching interview prompt from API')
    const prompt = await fetchInterviewPrompt(interviewId, token, otpProofToken)
    logger.info('Prompt fetched successfully')
    return prompt
  } catch (err) {
    logger.warn('Failed to fetch prompt, using default', { error: String(err) })
    return undefined
  }
}

async function loadQuestions(
  interviewId: string,
  token?: string,
  otpProofToken?: string
): Promise<InterviewQuestionContext[]> {
  try {
    const questionData = await fetchInterviewQuestions(interviewId, token, otpProofToken)
    const questions = questionData.map((question) => ({
      id: question.id,
      order: question.order ?? 0,
      summary: question.title,
      prompt: question.content,
    }))
    logger.info(`Fetched ${questions.length} questions for state tracking`)
    return questions
  } catch (err) {
    logger.warn('Failed to fetch questions for state tracking', { error: String(err) })
    return []
  }
}
